import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Employee } from '../../models/employee/employee.model';
import { CreateEmployee } from '../../models/employee/create-employee.model';
import { EditEmployee } from '../../models/employee/edit-employee.model';
import { SalaryUnlockService } from './salary-unlock.service';

@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  constructor(
    private http: HttpClient,
    private salaryUnlock: SalaryUnlockService,
  ) {}

  getEmployees(): Observable<{ employees: Employee[] }> {
    return this.http.get<{ employees: Employee[] }>(environment.employee.root, { withCredentials: true });
  }

  getEmployeeByGuid(employeeGuid: string): Observable<{ employee: Employee | null }> {
    const url = `${environment.employee.root}/${employeeGuid}`;
    return this.http.get<{ employee: Employee | null }>(url, {
      withCredentials: true,
      headers: this.salaryHeaders(this.salaryUnlock.tokenForEmployee(employeeGuid)),
    });
  }

  createEmployee(payload: CreateEmployee): Observable<{ employee: Employee }> {
    return this.http.post<{ employee: Employee }>(environment.employee.root, payload, {
      withCredentials: true,
      headers: this.salaryHeaders(this.salaryUnlock.composeUnlockToken()),
    });
  }

  assignMinimumWageRate(employeeGuid: string, minimumWageRateId: number | null): Observable<{ employee: Employee }> {
    return this.http.put<{ employee: Employee }>(
      `${environment.employee.root}/${employeeGuid}/minimum-wage-rate`,
      { minimumWageRateId },
      { withCredentials: true },
    );
  }

  editEmployee(employeeGuid: string, payload: EditEmployee): Observable<{ employee: Employee }> {
    const url = `${environment.employee.root}/${employeeGuid}`;
    return this.http.put<{ employee: Employee }>(url, payload, {
      withCredentials: true,
      headers: this.salaryHeaders(this.salaryUnlock.tokenForEmployee(employeeGuid)),
    });
  }

  deleteEmployee(employeeGuid: string): Observable<void> {
    const url = `${environment.employee.root}/${employeeGuid}`;
    return this.http.delete<void>(url, { withCredentials: true });
  }

  uploadProfilePhoto(employeeGuid: string, file: File): Observable<{ employee: Employee }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<{ employee: Employee }>(
      environment.employee.profilePhoto(employeeGuid),
      formData,
      { withCredentials: true },
    );
  }

  clearProfilePhoto(employeeGuid: string): Observable<{ employee: Employee }> {
    return this.http.delete<{ employee: Employee }>(
      environment.employee.profilePhoto(employeeGuid),
      { withCredentials: true },
    );
  }

  private salaryHeaders(token: string | null): HttpHeaders {
    return token ? new HttpHeaders({ 'X-Salary-Unlock': token }) : new HttpHeaders();
  }
}
